import React from "react";

import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import Skeleton from "@mui/material/Skeleton";

import { TableHeadCell } from "components/Table/types";

interface SkeletonListItemsProps<T> {
  heading: TableHeadCell<T>[];
  body: TableHeadCell<T>[];
  rowCount?: number;
  withActions?: boolean;
  withDivider?: boolean;
}

const SkeletonListItems = <T extends { id: string }>(
  props: SkeletonListItemsProps<T>
) => {
  const { heading, body, rowCount = 5, withActions, withDivider } = props;

  const countWidth = (width: string | number | undefined) => {
    const screenWidth = window.innerWidth;
    const defaultWidth = screenWidth * 0.2;

    if (!width) return defaultWidth;

    if (typeof width === "number") return width;

    if (width.includes("%")) {
      const parsedWidth = parseFloat(width);
      return isNaN(parsedWidth) ? defaultWidth : `${parsedWidth * 2}%`;
    }

    return defaultWidth;
  };

  return (
    <>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <ListItem key={`skeleton-row-${rowIndex}`}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ width: "100%" }}>
                {heading.map(({ width }, index) => (
                  <Skeleton
                    key={`sh-${rowIndex}-${index}`}
                    animation="wave"
                    sx={{ width: countWidth(width) }}
                  />
                ))}
              </Box>
              {withActions && (
                <Skeleton
                  animation="wave"
                  sx={{ width: "24px", height: "24px" }}
                />
              )}
            </Box>
            {withDivider && (
              <Box sx={{ border: "1px solid #E5E5E5", mt: 1.5, mb: 1.5 }} />
            )}
            <Box width="100%">
              {body.map(({ width }, index) => (
                <Skeleton
                  key={`sb-${rowIndex}-${index}`}
                  animation="wave"
                  sx={{ width: countWidth(width) }}
                />
              ))}
            </Box>
          </Box>
        </ListItem>
      ))}
    </>
  );
};

export default SkeletonListItems;
